import React, { useEffect, useState } from "react";
import { z } from "zod";
import { api, extractApiError } from "@/lib/api";
import { checkoutReturnFields } from "@/lib/tenantPath";
import { checkoutErrorMessage } from "@/lib/checkoutErrors";
import { currentRef } from "../lib/affiliateRef";
// --- crm-module BEGIN C3.04 (marketplace attribution -- write side) ---
import { currentPromotionRef } from "../lib/promotionRef";
// --- crm-module END ---
import { toast } from "sonner";
import { useLanguage, useT, useLocalized } from "@/lib/LanguageContext";
import { useRequireAuth } from "@/components/auth/useRequireAuth";
import ThemedPageLayout from "@/templates/ThemedPageLayout";
import PurchasePanel from "@/components/checkout/PurchasePanel";
import { ArrowUpRight, Gift, ShoppingBag, Loader2 } from "lucide-react";
// Relative, not "@/...": real modules under Jest, no alias there.
import useFieldErrors from "../hooks/useFieldErrors";
import FieldError, { invalidInputClass } from "../components/ui/FieldError";
import { emailField } from "../lib/validation";

const giftCardFormSchema = z.object({ email: emailField });

// No default currency — see Programmes.jsx: the server fills `currency` with what the
// checkout would charge, and an invented "cad" here misdescribed rows now charged in USD.
const money = (cents, currency) => `${(cents / 100).toFixed(2)} ${String(currency || "").toUpperCase()}`.trim();

// Same law as Programmes.jsx `purchasable`: a positive price (BUG-046) AND not flagged
// unsellable by the server (a currency the platform no longer settles). Callers keep the
// affiliate exemption OUTSIDE this predicate: a partner-link row is never paid online here.
const purchasable = (p) => p.price_cents > 0 && p.payable_online !== false;

export default function Boutique() {
  const { lang } = useLanguage();
  const t = useT("public");
  const tc = useT("common");
  const loc = useLocalized();
  const { ensureAuthed, authDialog } = useRequireAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [giftOpen, setGiftOpen] = useState(false);
  // THE PRODUCT THE BUYER CHOSE, and the only thing that opens the purchase panel
  // (BUG-049). The promo code and the gift card used to live HERE, above the whole grid:
  // one pair of fields for a whole catalogue, so a code could be accepted with no product
  // to apply it to, a card could only ever show its balance, and both were handed the
  // currency of the FIRST stripe row rather than of the row being bought. They now belong
  // to `PurchasePanel`, which knows all three.
  //
  // Still deliberately NOT offered inside `GiftCardModal`: a gift card is stored value,
  // discounting it is arbitrage (CA$80 buying CA$100 of spendable balance) and paying for
  // one WITH another mints a fresh instrument out of one already accounted for. The server
  // refuses both outright (modules/promos.DISCOUNTABLE_KINDS,
  // giftcards.CHECKOUT_ORDER_KINDS); this is the UI saying the same thing.
  const [chosen, setChosen] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/shop/products");
        setProducts(data);
      } finally { setLoading(false); }
    })();
  }, []);

  const buy = async (p, { promoCode = "", giftCardCode = "", marketingOptIn = false } = {}) => {
    if (p.type === "affiliate") {
      window.open(p.affiliate_url, "_blank", "noopener,noreferrer");
      return;
    }
    // Defense in depth: the Buy button never renders for an unpurchasable row (see the
    // grid below), but `buy` refuses it directly too rather than trusting the render path.
    // "Unbuyable unless proven otherwise": a legacy/unrecognised `type` or an ABSENT
    // `price_cents` (`undefined <= 0` is `false`) must fail this check, not pass it.
    if (p.type !== "affiliate" && !purchasable(p)) return;
    const u = await ensureAuthed();
    if (!u) return;
    setBusy(p.id);
    try {
      const { data } = await api.post("/checkout/product", {
        product_id: p.id,
        origin_url: window.location.origin,
        ...checkoutReturnFields("/merci/boutique", "/boutique"),
        // THE BUYER'S LANGUAGE (BUG-106). Without it the checkout fell back to the
        // PRACTICE's locale, so the payment page and the confirmation email named the
        // product — and addressed the buyer — in the practitioner's language.
        locale: lang,
        // Omitted entirely when the buyer typed nothing — see Programmes.jsx.
        ...(promoCode ? { promo_code: promoCode } : {}),
        ...(giftCardCode ? { gift_card_code: giftCardCode } : {}),
        // Attribution (contract §2): the referral code the visitor arrived with, or "".
        // Sent on every capturable checkout so the sale can be traced back to whoever
        // introduced the buyer; the server resolves it and silently ignores an invalid,
        // self-referring or expired one — a checkout never fails on a query parameter the
        // buyer did not choose.
        ref: currentRef(),
        // --- crm-module BEGIN C3.04 (marketplace attribution -- write side) ---
        promotion_id: currentPromotionRef() || undefined,
        // --- crm-module END ---
        // The buyer's answer to the panel's optional marketing box (false untouched);
        // recorded by the server only once the sale is paid.
        marketing_opt_in: marketingOptIn === true,
      });
      window.location.href = data.url;
    } catch (e) {
      // extractApiError alone would surface the backend's ENGLISH diagnostic message to
      // an FR/ES buyer; the code is what carries the meaning, so translate that first.
      // THE APP'S OWN SURFACE, never `alert()`. A native dialog blocks the renderer
      // until it is dismissed (the row underneath stays stuck in its busy state), cannot
      // be themed inside a practitioner's storefront, and can be suppressed by the
      // browser after a repeat — at which point the NEXT failure really is invisible.
      // The message itself is unchanged: `checkoutErrorMessage` maps the server's stable
      // CODE to a translated sentence, because `extractApiError` alone would surface the
      // backend's ENGLISH diagnostic to an FR/ES buyer.
      toast.error(checkoutErrorMessage(e, t, t("shop.toast.error")));
    } finally {
      setBusy(null);
    }
  };

  return (
    <ThemedPageLayout>
      <section className="pt-32 pb-12 max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <span className="eyebrow">{t("shop.eyebrow")}</span>
            <h1 className="font-display text-5xl md:text-6xl mt-3 leading-tight">{t("shop.heading")}</h1>
            <p className="mt-3 text-eh-muted max-w-2xl">
              {t("shop.subheading")}
            </p>
          </div>
          <button onClick={() => setGiftOpen(true)} className="btn-ghost text-sm" data-testid="open-giftcard">
            <Gift size={14} /> {t("shop.giftButton")}
          </button>
        </div>

        <div className="mt-12">
          {loading ? (
            <div className="text-eh-muted flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> {tc("actions.loading")}</div>
          ) : products.length === 0 ? (
            <div className="text-eh-muted text-sm bg-eh-cream border border-eh-border-soft rounded-2xl p-10 text-center">
              {t("shop.empty")}
            </div>
          ) : (
            <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {products.map((p) => {
                const name = loc(p, "name");
                const desc = loc(p, "description");
                // BUG-046 — a stripe-checkout product with no real price is a pricing
                // defect, not a free giveaway: LearnPlayer's `free` course concept is a
                // deliberate, dedicated path (its own "enrol for free" button and copy).
                // Here there is no such path, so a `price_cents <= 0` item must not be
                // purchasable at all — not silently checked out at CA$0.00.
                //
                // Written as the complement of "affiliate", not as "stripe with a bad
                // price": `p.type === "stripe" && p.price_cents <= 0` let an unrecognised
                // legacy `type` OR an absent `price_cents` (`undefined <= 0` is `false`)
                // both render a Buy button — the exact trap this rule exists to close.
                //
                // `purchasable` adds the second server-stated reason — a currency the
                // platform no longer settles — under the same exemption.
                const unavailable = p.type !== "affiliate" && !purchasable(p);
                return (
                  <div key={p.id} className="bg-eh-cream border border-eh-border-soft rounded-3xl overflow-hidden flex flex-col" data-testid={`product-${p.id}`}>
                    {p.image_url && <img src={p.image_url} alt={name} className="aspect-[4/3] w-full object-cover" />}
                    <div className="p-5 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-eh-sage-deep">
                        {p.type === "affiliate" ? t("shop.productType.affiliate") : t("shop.productType.own")}
                      </div>
                      <h3 className="font-display text-2xl text-eh-ink mt-1">{name}</h3>
                      {desc && <p className="text-sm text-eh-muted mt-2 line-clamp-3">{desc}</p>}
                      <div className="mt-auto pt-5 flex items-center justify-between">
                        {p.type === "stripe" ? (
                          <div className="font-display text-xl text-eh-ink">{money(p.price_cents, p.currency)}</div>
                        ) : (
                          <div className="text-xs text-eh-muted">{t("shop.viaPartner")}</div>
                        )}
                        {unavailable ? (
                          // Reuses the existing money-path refusal copy (checkoutErrors.js
                          // CHECKOUT_ERROR_KEYS.INVALID_PRICE): same meaning — "this price
                          // cannot be paid online" — already translated in all 3 locales.
                          <span className="text-xs text-eh-muted italic" data-testid={`unavailable-${p.id}`}>
                            {t("checkout.error.invalidPrice")}
                          </span>
                        ) : (
                          <button
                            // An affiliate row is not bought here at all — it opens the
                            // partner's own site, and there is nothing of ours to price,
                            // discount or tax. Every other row goes through the panel.
                            onClick={() => (p.type === "affiliate" ? buy(p) : setChosen(p))}
                            disabled={busy === p.id}
                            className={`btn-primary text-xs ${busy === p.id ? "opacity-60" : ""}`}
                            data-testid={`buy-${p.id}`}
                          >
                            {busy === p.id ? <Loader2 size={12} className="animate-spin" /> : (p.type === "affiliate" ? <ArrowUpRight size={12} /> : <ShoppingBag size={12} />)}
                            {p.type === "affiliate" ? t("shop.viewProduct") : t("shop.buy")}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            </>
          )}
        </div>
      </section>
      {/* The chosen product, its full cost and the instruments for it (BUG-049).
          One dialog per page, never one per tile. */}
      {chosen && (
        <PurchasePanel
          name={loc(chosen, "name")}
          priceCents={chosen.price_cents}
          currency={chosen.currency}
          kind="product"
          offeringType="product"
          offeringId={chosen.id}
          payLabel={t("shop.buy")}
          busy={busy === chosen.id}
          onPay={(codes) => buy(chosen, codes)}
          onClose={() => setChosen(null)}
        />
      )}
      {giftOpen && <GiftCardModal onClose={() => setGiftOpen(false)} />}
      {authDialog}
    </ThemedPageLayout>
  );
}

const TIERS = ["25", "50", "100", "150", "200"];

function GiftCardModal({ onClose }) {
  const { lang } = useLanguage();
  const t = useT("public");
  const tc = useT("common");
  const { ensureAuthed, authDialog } = useRequireAuth();
  const [tier, setTier] = useState("50");
  const [custom, setCustom] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { fieldErrors, validate, clearFieldError } = useFieldErrors();

  const buy = async () => {
    // Judged locally, BEFORE the auth round trip: a blank/malformed recipient address
    // is this form's own mistake to answer (house rule: validate on click, no disabling
    // the button for it).
    if (validate(giftCardFormSchema, { email })) return;
    // Auth gates the PURCHASE, not the recipient: `recipient_email` below stays
    // whatever the buyer typed — it names who receives the card, not who bought
    // it, and the checkout endpoint carries no buyer-identity field to force.
    const u = await ensureAuthed();
    if (!u) return;
    setError(""); setBusy(true);
    try {
      const payload = {
        tier: custom ? null : tier,
        custom_amount_cents: custom ? Math.round(parseFloat(custom) * 100) : null,
        recipient_email: email,
        recipient_name: name,
        message: msg,
        origin_url: window.location.origin,
        // Cancel returns to the shop the buyer opened the modal from.
        ...checkoutReturnFields("/merci/carte-cadeau", "/boutique"),
        locale: lang,
        // Carried even though a gift-card PURCHASE earns the program nothing (contract
        // §10.1: the platform takes no commission on stored value, so the base is zero and
        // no commission row is written at all). The ref travels so that it is still in
        // storage — and still attributable — for the order that later REDEEMS the card,
        // which is the one that carries a commission.
        ref: currentRef(),
        // --- crm-module BEGIN C3.04 (marketplace attribution -- write side) ---
        promotion_id: currentPromotionRef() || undefined,
        // --- crm-module END ---
      };
      const { data } = await api.post("/checkout/giftcard", payload);
      window.location.href = data.url;
    } catch (e) {
      setError(extractApiError(e, t("shop.giftcard.toast.error")));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-eh-bg rounded-3xl max-w-lg w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="giftcard-modal">
        <h2 className="font-display text-3xl">{t("shop.giftcard.title")}</h2>
        <p className="text-eh-muted text-sm mt-1">{t("shop.giftcard.subtitle")}</p>
        <div className="mt-6">
          <label className="text-xs uppercase tracking-[0.18em] text-eh-muted">{t("shop.giftcard.amount")}</label>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {TIERS.map((t) => (
              <button key={t} onClick={() => { setTier(t); setCustom(""); }} className={`px-3 py-1.5 rounded-full text-sm border ${tier === t && !custom ? "bg-eh-ink text-eh-bg border-eh-ink" : "border-eh-border-soft hover:bg-eh-cream"}`}>{t} $</button>
            ))}
            <input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder={t("shop.giftcard.customAmountPh")} type="number" min="10" className="px-3 py-1.5 rounded-full text-sm border border-eh-border-soft w-28" />
          </div>
        </div>
        <div className="mt-5 space-y-3">
          <div>
            <input
              placeholder={t("shop.giftcard.recipientEmailPh")}
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); clearFieldError("email"); }}
              className={`w-full bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm ${fieldErrors.email ? invalidInputClass : ""}`}
              data-testid="giftcard-email"
            />
            <FieldError message={fieldErrors.email ? tc(fieldErrors.email) : ""} testId="giftcard-email-error" />
          </div>
          <input placeholder={t("shop.giftcard.recipientNamePh")} value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm" />
          <textarea placeholder={t("shop.giftcard.messagePh")} rows={3} value={msg} onChange={(e) => setMsg(e.target.value)} className="w-full bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm resize-none" />
        </div>
        {error && <div className="mt-3 text-sm text-red-600">{error}</div>}
        <div className="mt-6 flex items-center justify-end gap-3">
          <button onClick={onClose} className="btn-ghost text-sm">{tc("actions.cancel")}</button>
          {/* Content validity (blank/malformed email) is judged on click by buy() itself
              — only the in-flight request is a structural blocker (house rule: never
              disable for content validity). */}
          <button onClick={buy} disabled={busy} className="btn-primary text-sm disabled:opacity-60" data-testid="giftcard-checkout">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Gift size={14} />} {t("shop.giftcard.submit")}
          </button>
        </div>
        {/* Rendered inside the stopPropagation'd modal content, not as a sibling: the
            backdrop's onClick={onClose} would otherwise also fire for clicks inside
            this nested dialog (React portals bubble through the React tree). */}
        {authDialog}
      </div>
    </div>
  );
}
